import { useCallback, useEffect, useRef } from 'react';

import { DebouncedFunc } from 'lodash';
import throttle from 'lodash/throttle';

const MINUTE = 60 * 1000;

// if the user has been inactive for 30 minutes we log him out
const INACTIVITY_COUNTDOWN = 30 * MINUTE;

// if the ipad has not been touched in 25 minutes we give the user a chance to extend the session
const WARNING_COUNTDOWN = 25 * MINUTE;

// throttle to only fire once every second
const THROTTLE_INTERVAL = 1000;

// keydown is added just in case some users have hardware keyboards added to pad
// touch start already entails scrolling
const EVENTS = ['touchstart', 'keydown'];

type UseInactivityLogoutProps = {
    onLogout: () => void;
    onWarning: () => void;
};

export function useInactivityLogout({ onLogout, onWarning }: UseInactivityLogoutProps) {
    const timeoutRef = useRef<number | null>(null);
    const warningTimeoutRef = useRef<number | null>(null);
    const logoutAtRef = useRef<number | null>(null);
    // once the warning phase we stop reacting to user interaction
    // the user needs to either close the modal or explicitly state he wants to be logged in
    const phaseRef = useRef<'idle' | 'warning'>('idle');
    const throttledResetRef = useRef<DebouncedFunc<() => void> | null>(null);

    const resetTimer = useCallback(() => {
        if (phaseRef.current === 'warning') {
            return;
        }
        if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
        if (warningTimeoutRef.current) window.clearTimeout(warningTimeoutRef.current);

        timeoutRef.current = window.setTimeout(() => {
            onLogout();
        }, INACTIVITY_COUNTDOWN);

        const logoutAt = Date.now() + INACTIVITY_COUNTDOWN;
        logoutAtRef.current = logoutAt;

        warningTimeoutRef.current = window.setTimeout(() => {
            onWarning();
            phaseRef.current = 'warning';
        }, WARNING_COUNTDOWN);
    }, [onLogout, onWarning]);

    const reset = useCallback(() => {
        phaseRef.current = 'idle';
        resetTimer();
    }, [resetTimer]);

    useEffect(() => {
        throttledResetRef.current = throttle(resetTimer, THROTTLE_INTERVAL, { leading: true, trailing: false });
        const handler = () => throttledResetRef.current?.();

        EVENTS.forEach((event) => window.addEventListener(event, handler, { passive: true }));

        return () => {
            throttledResetRef.current?.cancel();
            EVENTS.forEach((event) => window.removeEventListener(event, handler));
        };
    }, [resetTimer]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                window.clearTimeout(timeoutRef.current);
            }
            if (warningTimeoutRef.current) {
                window.clearTimeout(warningTimeoutRef.current);
            }
        };
    }, []);

    return { reset, logoutAt: logoutAtRef.current };
}
